import React from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Stack,
    Divider,
    Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'; // Icono para el video
import QrCode2Icon from '@mui/icons-material/QrCode2'; // Icono pro para el QR

// 1. Importamos la librería del QR
import { QRCodeCanvas } from 'qrcode.react';

export default function HelpSection() {

    // Definimos la URL en una constante para no repetirla
    const URL_FORMULARIO = 'https://red.cafira.com/formulario-suscripcion-personalizado';
    const URL_VIDEO = 'https://cafira.sfo2.digitaloceanspaces.com/tutoriales/red-cafira-tutorial-formulario-personalizado.mp4';
    const GRIS_CORPORATIVO = '#353535';

    return (
        <Box sx={{ mt: 3, width: '100%' }}>
            <Accordion
                elevation={0}
                variant="outlined"
                sx={{
                    borderRadius: '8px !important',
                    overflow: 'hidden',
                    borderColor: 'divider'
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: GRIS_CORPORATIVO }} />}
                    aria-controls="panel-ayuda-content"
                    id="panel-ayuda-header"
                    sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.01)', // Fondo aún más sutil
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '& .MuiAccordionSummary-content': {
                            alignItems: 'center',
                            gap: 1.5
                        }
                    }}
                >
                    <HelpOutlineIcon fontSize="small" sx={{ color: GRIS_CORPORATIVO }} />
                    <Typography sx={{ fontWeight: 600, color: GRIS_CORPORATIVO }}>
                        ¿Necesitas ayuda con las suscripciones?
                    </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 4, backgroundColor: '#fff' }}>
                    <Stack spacing={2}>
                        {/* Contenedor Flex para layout de 2 columnas en escritorio, 1 en móvil */}
                        <Stack
                            direction={{ xs: 'column-reverse', md: 'row' }}
                            spacing={4}
                            divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />}
                        >

                            {/* COLUMNA IZQUIERDA: Texto instructivo */}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }} textAlign="left">
                                    Este módulo gestiona los formularios de suscripción pública accesibles desde la siguiente URL exclusiva:

                                    <Box
                                        component="code"
                                        sx={{
                                            display: 'block',
                                            my: 2,
                                            p: 1.5,
                                            backgroundColor: 'grey.50',
                                            border: '1px dashed',
                                            borderColor: 'grey.300',
                                            borderRadius: 1,
                                            color: GRIS_CORPORATIVO,
                                            fontWeight: 700,
                                            fontSize: '0.85rem',
                                            textAlign: 'center',
                                            wordBreak: 'break-all' // Evita que la URL rompa el diseño en móvil
                                        }}
                                    >
                                        {URL_FORMULARIO}
                                    </Box>

                                    <Typography variant="subtitle2" sx={{ color: 'text.primary', mb: 1, mt: 3, fontWeight: 700 }}>
                                        Reglas operativas principales:
                                    </Typography>

                                    <ul style={{ paddingLeft: '1.2rem', margin: '0' }}>
                                        <li><strong>Exclusividad:</strong> Solo puede haber <strong>un (1) formulario activo</strong> en simultáneo.</li>
                                        <li style={{ marginTop: '8px' }}><strong>Sincronización PagoTic:</strong> El título y el importe definidos aquí se reflejarán directamente en la pasarela de pagos al momento de la suscripción.</li>
                                        <li style={{ marginTop: '8px' }}><strong>Validación Temporal:</strong> Fuera del rango de fechas/horas programado, el acceso al formulario público estará bloqueado.</li>
                                        <li style={{ marginTop: '8px' }}><strong>Meses "Bonificados":</strong> Para gestionar el fin de promociones (ej. de $1 a precio real), la modificación debe realizarse manualmente desde el panel de PagoTic.</li>
                                    </ul>
                                </Typography>
                            </Box>

                            {/* COLUMNA DERECHA: Código QR */}
                            <Stack
                                spacing={2}
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                    p: 3,
                                    backgroundColor: 'grey.50',
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'grey.100',
                                    minWidth: '220px' // Asegura espacio para el QR
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                                    <QrCode2Icon color="primary" fontSize="small" />
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                        Acceso Rápido Móvil
                                    </Typography>
                                </Box>

                                {/* Contenedor blanco para el QR (para mejor contraste) */}
                                <Paper
                                    elevation={1}
                                    sx={{
                                        p: 1.5,
                                        lineHeight: 0,
                                        borderRadius: 1,
                                        bgcolor: 'background.paper'
                                    }}
                                >
                                    <QRCodeCanvas
                                        value={URL_FORMULARIO} // La URL dinámica
                                        size={160}           // Tamaño en píxeles
                                        bgColor={"#ffffff"}   // Fondo
                                        fgColor={"#353535"}   // Color del QR (puedes usar color.primary de MUI)
                                        level={"H"}           // Nivel de corrección de errores (H=Alta, permite logos o suciedad)
                                        includeMargin={false}
                                    />
                                </Paper>

                                <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ maxWidth: '180px' }}>
                                    Escanea este código para previsualizar el formulario activo en tu dispositivo.
                                </Typography>
                            </Stack>
                        </Stack>

                        <Divider />

                        {/* SECCIÓN INFERIOR: Video Tutorial */}
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <PlayCircleOutlineIcon sx={{ color: GRIS_CORPORATIVO }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: GRIS_CORPORATIVO }}>
                                    VIDEO TUTORIAL: CÓMO CONFIGURAR UNA SUSCRIPCIÓN
                                </Typography>
                            </Stack>

                            <Paper
                                elevation={0}
                                sx={{
                                    width: '100%',
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    backgroundColor: '#000', // Fondo negro para el video
                                    aspectRatio: '16/9' // Mantiene la proporción 16:9 automáticamente
                                }}
                            >
                                <video
                                    controls
                                    width="100%"
                                    height="100%"
                                    style={{ display: 'block', objectFit: 'contain' }}
                                >
                                    <source src={URL_VIDEO} type="video/mp4" />
                                    Tu navegador no soporta videos HTML5.
                                </video>
                            </Paper>

                            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'grey.500', fontStyle: 'italic' }}>
                                * El video muestra el proceso desde la creación hasta la validación en PagoTic.
                            </Typography>
                        </Box>
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}